import { Close } from '@mui/icons-material';
import {
  Alert,
  AlertColor,
  AlertProps,
  Collapse,
  IconButton,
} from '@mui/material';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  NotificationContext,
  useNotifications,
} from '../hooks/useNotifications';

export type ArmhrNotificationOptions = {
  message: boolean | string | ReactNode;
  severity?: AlertColor;
  action?: ReactNode;
  autoHideDuration?: number;
};

export const NotificationProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notification, setNotification] =
    useState<ArmhrNotificationOptions | null>(null);

  useEffect(() => {
    if (isOpen && notification?.autoHideDuration) {
      const timer = setTimeout(
        clearNotificationHandler,
        notification.autoHideDuration
      );
      return () => clearTimeout(timer);
    }
  }, [isOpen, notification]);

  const showNotificationHandler = useCallback(
    (options: ArmhrNotificationOptions) => {
      setNotification(options);
      setIsOpen(true);
    },
    []
  );

  const clearNotificationHandler = () => {
    setIsOpen(false);
    setTimeout(() => {
      setNotification(null);
    }, 300);
  };

  return (
    <NotificationContext.Provider
      value={{
        showNotification: showNotificationHandler,
        isOpen,
        notification,
        clearNotification: clearNotificationHandler,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const GlobalNotification: React.FC<AlertProps> = (props) => {
  const { notification, isOpen, clearNotification } = useNotifications();
  const { pathname } = useLocation();

  const renderMessage = () => {
    if (notification?.message === true) {
      return (
        <>
          Sorry, something unexpected happened. Our team has been notified,
          please refresh and try again.
        </>
      );
    } else if (typeof notification?.message === 'string') {
      return notification.message;
    } else {
      return notification?.message;
    }
  };

  useEffect(() => {
    if (isOpen) {
      clearNotification();
    }
  }, [pathname]);

  return (
    <Collapse in={isOpen}>
      <Alert
        severity={notification?.severity}
        action={
          notification?.action || (
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={clearNotification}
            >
              <Close fontSize="inherit" />
            </IconButton>
          )
        }
        {...props}
      >
        {renderMessage()}
      </Alert>
    </Collapse>
  );
};
